<template>
  <el-dialog
    :visible.sync="innerVisible"
    :title="dialogTitle"
    :width="dialogWidth"
    :show-close="true"
    :close-on-click-modal="true"
    append-to-body
    class="bond-preview-dialog"
  >
    <div class="preview-content">
      <div v-if="loading" class="loading-container">
        <i class="el-icon-loading loading-icon" />
        <span>加载中...</span>
      </div>
      <div v-else-if="error" class="error-container">
        <i class="el-icon-error error-icon" />
        <span>{{ error }}</span>
      </div>
      <div v-else class="file-content">
        <div v-if="pdfSrc" class="pdf-container">
          <pdf v-for="i in numPages" :key="i" :src="pdfSrc" :page="i" />
        </div>
        <div v-if="wordSrc" class="office-wrapper word-wrapper">
          <vue-office-docx :src="wordSrc" :page-width="'100%'" :style="{ width: '100%', height: '500px' }" />
        </div>
        <div v-if="excelSrc" class="office-wrapper">
          <div class="office-container">
            <vue-office-excel :src="excelSrc" :style="{ width: '100%', minWidth: '1200px', height: '500px' }" />
          </div>
        </div>
        <div v-if="imgUrl" class="image-wrapper">
          <div class="image-controls">
            <el-button size="mini" @click="zoomOut">
              <i class="el-icon-minus" />
            </el-button>
            <span class="zoom-value">{{ zoomLevel }}%</span>
            <el-button size="mini" @click="zoomIn">
              <i class="el-icon-plus" />
            </el-button>
            <el-button size="mini" @click="resetZoom">重置</el-button>
          </div>
          <div class="image-container">
            <img
              :src="imgUrl"
              :alt="fileName"
              :style="{ transform: `scale(${zoomLevel / 100})`, transformOrigin: 'center center' }"
            >
          </div>
        </div>
        <div v-if="txtPre" class="txt-container">
          <pre>{{ txtPre }}</pre>
        </div>
        <div v-if="!pdfSrc && !wordSrc && !excelSrc && !imgUrl && !txtPre" class="unsupported-container">
          <i class="el-icon-info" />
          <span>暂不支持此文件类型预览，请下载查看</span>
        </div>
      </div>
    </div>
    <span slot="footer" class="dialog-footer">
      <el-button @click="handleClose">关闭</el-button>
    </span>
  </el-dialog>
</template>

<script>
import pdf from 'vue-pdf'
import { cache, cacheName } from '@/utils/cacheManager'
import { bondConvertFileByFileId, bondFindByFileId } from '@/api/bondManagement'
import vueOfficeExcel from '@vue-office/excel'
import vueOfficeDocx from '@vue-office/docx'
import '@vue-office/excel/lib/index.css'

export default {
  name: 'BondPreviewDialog',
  components: { pdf, vueOfficeExcel, vueOfficeDocx },
  props: {
    visible: { type: Boolean, required: true },
    fileId: { type: String, default: '' },
    fileName: { type: String, default: '' },
    fileFormat: { type: String, default: '' }
  },
  data() {
    return {
      innerVisible: false,
      loading: false,
      error: '',
      numPages: '',
      pdfSrc: '',
      imgUrl: '',
      txtPre: '',
      wordSrc: '',
      excelSrc: '',
      zoomLevel: 100
    }
  },
  computed: {
    dialogTitle() {
      return this.fileName || '文件预览'
    },
    dialogWidth() {
      const fileType = this.getFileType(this.fileName)
      const imgType = ['png', 'jpg', 'jpeg', 'gif', 'webp']
      if (imgType.includes(fileType)) {
        return '90%'
      }
      return '90%'
    }
  },
  watch: {
    visible(newVal) {
      this.innerVisible = newVal
      if (newVal) {
        this.loadFile()
        this.zoomLevel = 100
      } else {
        this.clearPreview()
      }
    },
    innerVisible(newVal) {
      this.$emit('update:visible', newVal)
    }
  },
  created() {
    if (this.visible) {
      this.innerVisible = true
      this.loadFile()
    }
  },
  beforeDestroy() {
    this.clearPreview()
  },
  methods: {
    clearPreview() {
      this.pdfSrc = ''
      this.wordSrc = ''
      this.excelSrc = ''
      if (this.imgUrl) {
        window.URL.revokeObjectURL(this.imgUrl)
        this.imgUrl = ''
      }
      this.txtPre = ''
      this.loading = false
      this.error = ''
      this.numPages = ''
      this.zoomLevel = 100
    },
    async loadFile() {
      this.clearPreview()
      if (!this.fileId) {
        this.error = '文件ID不能为空'
        return
      }
      this.loading = true
      try {
        await this.handlePreviewFile(this.fileId)
      } catch (e) {
        this.error = '文件加载失败'
        console.error('File preview error:', e)
      } finally {
        this.loading = false
      }
    },
    async handlePreviewFile(fileId) {
      const fileType = this.getFileType(this.fileName)
      await cache.load(cacheName.USER, cacheName.CONFIG)
      const imgType = ['png', 'jpg', 'jpeg', 'gif', 'webp']
      if (fileType === 'pdf') {
        const res = await bondFindByFileId(fileId)
        if (res && res.data) {
          const arrayBuffer = new Uint8Array(await res.data.arrayBuffer())
          const loadingTask = pdf.createLoadingTask(arrayBuffer)
          await loadingTask.promise.then(pdf => {
            this.numPages = pdf.numPages
          })
          this.pdfSrc = arrayBuffer
        }
      } else if (fileType === 'doc') {
        const res = await bondConvertFileByFileId(fileId, 'docx')
        if (res && res.data) {
          this.wordSrc = window.URL.createObjectURL(res.data)
        }
      } else if (fileType === 'docx') {
        const res = await bondFindByFileId(fileId)
        if (res && res.data) {
          this.wordSrc = window.URL.createObjectURL(res.data)
        }
      } else if (fileType === 'xls') {
        const res = await bondConvertFileByFileId(fileId, 'xlsx')
        if (res && res.data) {
          this.excelSrc = window.URL.createObjectURL(res.data)
        }
      } else if (fileType === 'xlsx') {
        const res = await bondFindByFileId(fileId)
        if (res && res.data) {
          this.excelSrc = window.URL.createObjectURL(res.data)
        }
      } else if (imgType.includes(fileType)) {
        const res = await bondFindByFileId(fileId)
        if (res && res.data) {
          this.imgUrl = window.URL.createObjectURL(res.data)
        }
      } else if (fileType === 'txt') {
        const res = await bondFindByFileId(fileId)
        if (res && res.data) {
          const data = res.data
          const reader = new FileReader()
          const self = this
          await new Promise((resolve) => {
            reader.onload = function() {
              self.txtPre = reader.result
              resolve()
            }
            reader.readAsText(data, 'utf-8')
          })
        }
      }
    },
    getFileType(fileName) {
      if (!fileName) return ''
      if (fileName.indexOf('.') === -1) {
        if (this.fileFormat.indexOf('docx') !== -1 || this.fileFormat.indexOf('word') !== -1 || this.fileFormat.indexOf('xls') !== -1 || this.fileFormat.indexOf('xlsx') !== -1 || this.fileFormat.indexOf('pptx') !== -1) {
          return 'officeType'
        } else if (this.fileFormat.indexOf('pdf') !== -1) {
          return 'pdf'
        } else if (this.fileFormat.indexOf('txt') !== -1) {
          return 'txt'
        }
      } else {
        const arr = fileName.split('.')
        return arr[arr.length - 1].toLowerCase()
      }
      return ''
    },
    zoomIn() {
      if (this.zoomLevel < 300) {
        this.zoomLevel += 25
      }
    },
    zoomOut() {
      if (this.zoomLevel > 25) {
        this.zoomLevel -= 25
      }
    },
    resetZoom() {
      this.zoomLevel = 100
    },
    handleClose() {
      this.innerVisible = false
    }
  }
}
</script>

<style lang="scss">
.bond-preview-dialog .office-wrapper.word-wrapper * {
  background: #ffffff !important;
}
.bond-preview-dialog .office-wrapper.word-wrapper .docx-page,
.bond-preview-dialog .office-wrapper.word-wrapper .doc-page {
  margin: 0 !important;
  padding: 0 !important;
  background: #ffffff !important;
  box-shadow: none !important;
  width: 100% !important;
}
.bond-preview-dialog .office-wrapper.word-wrapper .docx-preview,
.bond-preview-dialog .office-wrapper.word-wrapper .doc-preview {
  padding: 0 !important;
  background: #ffffff !important;
}
</style>

<style lang="scss" scoped>
.bond-preview-dialog {
  .preview-content {
    max-height: 600px;
    overflow-y: auto;
    padding: 10px;
  }

  .loading-container,
  .error-container,
  .unsupported-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 300px;
    color: #999;

    .loading-icon,
    .error-icon {
      font-size: 48px;
      margin-bottom: 16px;
    }

    .loading-icon {
      animation: spin 1s linear infinite;
    }

    .error-icon {
      color: #f56c6c;
    }
  }

  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }

  .pdf-container {
    display: flex;
    flex-direction: column;
    align-items: center;

    :deep(.vue-pdf__page) {
      margin-bottom: 10px;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    }
  }

  .office-wrapper {
    overflow-x: auto;
    overflow-y: auto;
    max-height: 550px;
    border: 1px solid #ebeef5;
    border-radius: 4px;
  }

  .office-wrapper.word-wrapper {
    overflow-x: hidden;
    overflow-y: auto;
    max-height: 550px;
    background: #ffffff !important;
    border: none !important;

    :deep(*) {
      background: #ffffff !important;
    }

    :deep(.vue-office-docx) {
      background: #ffffff !important;
      width: 100% !important;
    }

    :deep(.docx-preview),
    :deep(.doc-preview),
    :deep(.docx-container),
    :deep(.doc-container) {
      padding: 0 !important;
      background: #ffffff !important;
      width: 100% !important;
      margin: 0 !important;
    }

    :deep(.docx-page),
    :deep(.doc-page) {
      margin: 0 !important;
      padding: 10px 0 !important;
      background: #ffffff !important;
      box-shadow: none !important;
      max-width: 100% !important;
      width: 100% !important;
    }

    :deep(.docx-content),
    :deep(.doc-content) {
      background: #ffffff !important;
      width: 100% !important;
    }

    :deep(.docx-body),
    :deep(.doc-body) {
      padding: 0 !important;
      margin: 0 !important;
    }

    :deep(.page-container) {
      background: #ffffff !important;
      box-shadow: none !important;
      padding: 0 !important;
      margin: 0 !important;
    }
  }

  .office-container {
    min-width: 1200px;
  }

  .image-wrapper {
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  .image-controls {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 12px;
    padding: 8px 16px;
    background: #f5f5f5;
    border-radius: 4px;
  }

  .zoom-value {
    font-size: 14px;
    color: #666;
    min-width: 50px;
    text-align: center;
  }

  .image-container {
    overflow-x: auto;
    overflow-y: auto;
    max-height: 500px;
    width: 100%;
    background: #fafafa;
    border-radius: 4px;
    display: flex;
    justify-content: center;
    align-items: flex-start;

    img {
      max-width: none;
      max-height: none;
      transition: transform 0.2s ease;
    }
  }

  .txt-container {
    background: #fafafa;
    border: 1px solid #ebeef5;
    border-radius: 4px;
    padding: 16px;
    max-height: 500px;
    overflow-y: auto;

    pre {
      margin: 0;
      white-space: pre-wrap;
      word-break: break-all;
      font-family: 'Courier New', monospace;
      font-size: 14px;
      line-height: 1.6;
      color: #333;
    }
  }

  .dialog-footer {
    text-align: right;
    display: block;
  }
}
</style>
